/**
 * Ortak form doğrulayıcıları — e-posta, telefon (TR), tarih.
 *
 * Tasarım kararı (bkz. ResourceForm):
 *   - Boş + opsiyonel alan → GEÇERLİ (null döner). Zorunluluk kontrolü
 *     ResourceForm'un `required` mantığında ayrıca yapılır; buradaki
 *     doğrulayıcılar yalnızca DOLU değerin formatına bakar.
 *   - Hata → kullanıcıya gösterilecek Türkçe mesaj (string) döner.
 *
 * Manuel formlarda (ResourceForm kullanmayan) doğrudan
 * `validateEmail(v)` / `validatePhoneTR(v)` çağrılabilir; Mantine
 * useForm `validate` bloğuna birebir uyumludur (geçerli=null, hata=string).
 */

/** Boş kabul edilen değerler — opsiyonel alanlarda doğrulama atlanır. */
function isBlank(v: unknown): boolean {
  return v == null || (typeof v === "string" && v.trim() === "");
}

// RFC 5322'nin pratik alt kümesi. Tek @, boşluksuz, en az bir nokta
// içeren alan adı. Aşırı katı değil ama "ali@", "ali@x", "a b@x.com"
// gibi tipik hataları yakalar.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * E-posta doğrula. Boş + opsiyonel → geçerli.
 * @returns null (geçerli) | hata mesajı (string)
 */
export function validateEmail(value: unknown): string | null {
  if (isBlank(value)) return null;
  return EMAIL_RE.test(String(value).trim())
    ? null
    : "Geçerli bir e-posta adresi girin (örn. ad@site.com)";
}

/**
 * TR telefon numarasındaki tüm ayraçları (boşluk, tire, parantez, +)
 * temizleyip yalnızca rakamları döndürür.
 */
function digitsOnly(value: string): string {
  return value.replace(/\D/g, "");
}

/**
 * TR telefon numarasını doğrula. Kabul edilen girişler:
 *   +90 5XX XXX XX XX   →  905XXXXXXXXX (12 hane)
 *   0 5XX XXX XX XX     →  05XXXXXXXXX  (11 hane)
 *   5XX XXX XX XX       →  5XXXXXXXXX   (10 hane)
 * Boşluk / tire / parantez serbest. Yabancı numara reddedilir.
 *
 * Boş + opsiyonel → geçerli.
 * @returns null (geçerli) | hata mesajı (string)
 */
export function validatePhoneTR(value: unknown): string | null {
  if (isBlank(value)) return null;
  const d = digitsOnly(String(value));
  // Olası önekleri soyup 10 haneli "5XXXXXXXXX" çekirdeğine indir.
  let core = d;
  if (core.length === 12 && core.startsWith("90")) core = core.slice(2);
  else if (core.length === 11 && core.startsWith("0")) core = core.slice(1);
  // TR cep/sabit hat: 10 hane ve "5" (cep) ya da "2"/"3"/"4" (sabit) ile başlar.
  if (core.length === 10 && /^[2-5]\d{9}$/.test(core)) return null;
  return "Geçerli bir telefon girin (örn. +90 555 123 45 67)";
}

/**
 * TR telefonu kanonik +90XXXXXXXXXX biçimine getirir. Doğrulamadan
 * geçmiş değer beklenir; geçersizse girişi olduğu gibi döndürür.
 * Backend'e tek tip göndermek için ResourceForm submit'inde kullanılır.
 */
export function normalizePhoneTR(value: unknown): string {
  if (isBlank(value)) return "";
  const d = digitsOnly(String(value));
  let core = d;
  if (core.length === 12 && core.startsWith("90")) core = core.slice(2);
  else if (core.length === 11 && core.startsWith("0")) core = core.slice(1);
  if (core.length === 10 && /^[2-5]\d{9}$/.test(core)) return `+90${core}`;
  return String(value);
}

/**
 * Tarih değerini doğrula. DateInput bir `Date` ya da boş döndürür;
 * elle "YYYY-MM-DD" string'i de gelebilir. Geçersiz tarih reddedilir.
 *
 * Boş + opsiyonel → geçerli.
 * @returns null (geçerli) | hata mesajı (string)
 */
export function validateDate(value: unknown): string | null {
  if (isBlank(value)) return null;
  const date = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(date.getTime())
    ? "Geçerli bir tarih seçin"
    : null;
}
